import clsx from 'clsx';
import { DialogPassThroughOptions } from 'primereact/dialog';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../Button/Button';
import { Icon } from '../../Icon/Icon';
import { DialogSize } from '../constraints';
import { ConfirmCloseConfig, DialogButtonsConfig } from '../types';
import { CloseConfirmationDialog } from './CloseConfirmationDialog';
import {
    DialogWithDoubleEscape,
    useDialogClose,
} from './DialogWithDoubleEscape';

interface FormDialogProps {
    title?: ReactNode;
    subtitle?: ReactNode;
    children: ReactNode;
    onClose: () => void;
    size: DialogSize;
    buttons?: DialogButtonsConfig;
    loading: boolean;
    disabled: boolean;
    onSubmit?: () => void;
    pt?: DialogPassThroughOptions;
    confirmClose?: ConfirmCloseConfig;
    dialogClassName?: string;
}

export function FormDialog({
    title,
    subtitle,
    children,
    onClose,
    size,
    buttons,
    loading,
    onSubmit,
    disabled,
    pt,
    confirmClose,
    dialogClassName: propsDialogClassName,
}: FormDialogProps): ReactNode {
    const [confirmVisible, setConfirmVisible] = useState(false);
    const closeRef = useRef<(() => void) | undefined>(undefined);
    const dialogClassName = clsx('dialog-form', size, propsDialogClassName);
    const buttonsClassName = clsx(
        'flex flex-row gap-3',
        size === DialogSize.sm ? 'flex-row-reverse' : null,
    );

    const forceClose = useCallback((): void => {
        (closeRef.current ?? onClose)();
    }, [onClose]);

    const handleRequestClose = (): void => {
        if (confirmClose?.isDirty()) {
            setConfirmVisible(true);
        } else {
            forceClose();
        }
    };

    function HeaderContent(): ReactNode {
        const close = useDialogClose();
        const closeButton = buttons?.closeButton;
        const buttonsTemplate = (
            <>
                <Button
                    data-testid="dialog-submit"
                    loading={loading}
                    disabled={disabled}
                    label={buttons?.submitLabel ?? 'Save'}
                    icon={buttons?.submitIcon}
                    iconPos={buttons?.submitIconPos}
                    onClick={() => {
                        onSubmit?.();
                    }}
                />
                <Button
                    data-testid="dialog-close"
                    disabled={loading}
                    label={closeButton?.label ?? 'Close'}
                    severity="secondary"
                    onClick={() => {
                        if (closeButton?.callback) {
                            closeButton.callback();
                        }

                        if (!closeButton?.overrideDefault) {
                            close();
                        }
                    }}
                />
            </>
        );

        return (
            <div className="flex flex-row justify-content-between align-items-center">
                <div className="flex flex-column gap-1">
                    <div className="flex align-items-center">{title}</div>
                    {subtitle ? (
                        <div className="text-secondary text-sm font-normal text-transform-none">
                            {subtitle}
                        </div>
                    ) : null}
                </div>
                {[DialogSize.md, DialogSize.lg].includes(size) ? (
                    <div className={buttonsClassName}>
                        {buttons?.content ?? null}
                        {!buttons?.content && buttonsTemplate}
                    </div>
                ) : (
                    <Button
                        icon={<Icon name="cancel" fontSize="normal" />}
                        rounded
                        text
                        onClick={() => {
                            if (closeButton?.callback) {
                                closeButton.callback();
                            }

                            if (!closeButton?.overrideDefault) {
                                close();
                            }
                        }}
                    />
                )}
            </div>
        );
    }

    function FooterContent(): ReactNode {
        const close = useDialogClose();
        const closeButton = buttons?.closeButton;
        return (
            <>
                <Button
                    data-testid="dialog-submit"
                    loading={loading}
                    disabled={disabled}
                    label={buttons?.submitLabel ?? 'Save'}
                    icon={buttons?.submitIcon}
                    iconPos={buttons?.submitIconPos}
                    onClick={() => {
                        onSubmit?.();
                    }}
                />
                <Button
                    data-testid="dialog-close"
                    disabled={loading}
                    label={closeButton?.label ?? 'Close'}
                    severity="secondary"
                    onClick={() => {
                        if (closeButton?.callback) {
                            closeButton.callback();
                        }

                        if (!closeButton?.overrideDefault) {
                            close();
                        }
                    }}
                />
            </>
        );
    }

    return (
        <>
            <DialogWithDoubleEscape
                onClose={onClose}
                onBeforeClose={confirmClose ? handleRequestClose : undefined}
                doubleEscapeEnabled={!confirmVisible}
                className={dialogClassName}
                header={<HeaderContent />}
                footer={size !== DialogSize.sm ? undefined : <FooterContent />}
                pt={pt}
                closeRef={closeRef}
            >
                {children}
            </DialogWithDoubleEscape>
            {confirmClose && (
                <CloseConfirmationDialog
                    visible={confirmVisible}
                    config={confirmClose}
                    onCancel={() => setConfirmVisible(false)}
                    onConfirm={() => {
                        setConfirmVisible(false);
                        forceClose();
                    }}
                />
            )}
        </>
    );
}
